import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ATALHOS_DE_PERFIL, ROTULO_RECURSO, type Recurso } from '@arena/shared/permissoes';
import type { PerfilNaMatriz } from '@arena/shared/schemas';
import { perfisApi } from '../api/acessos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario } from '../ui/primitivos.jsx';

/**
 * `#m-perfil` — novo perfil, em branco ou copiando outro.
 *
 * As "Permissões iniciais" são os atalhos de `@arena/shared/permissoes`
 * — o MESMO catálogo que o teste de regressão do documento 19 usa para
 * criar o "Atendente do bar": o nível de operação, nunca "Total". Quem
 * precisa de mais afina depois, célula a célula, na matriz.
 *
 * Comanda e Estoque nascem marcados, como no protótipo — é o exemplo
 * que o próprio protótipo usa ("Atendente do bar").
 */
const PRE_SELECIONADOS: readonly Recurso[] = ['comanda', 'estoque'];

export function ModalDeNovoPerfil({
  perfis,
  onFechar,
  onCriado,
}: {
  perfis: readonly PerfilNaMatriz[];
  onFechar: () => void;
  onCriado: () => void;
}) {
  const [nome, setNome] = useState('');
  const [origemId, setOrigemId] = useState('');
  const [selecionados, setSelecionados] = useState<readonly Recurso[]>(PRE_SELECIONADOS);
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => {
      const niveis = Object.fromEntries(
        ATALHOS_DE_PERFIL.filter((a) => selecionados.includes(a.recurso)).map((a) => [
          a.recurso,
          a.nivel,
        ]),
      );
      return perfisApi.criar({
        nome: nome.trim(),
        ...(origemId ? { origemId } : {}),
        niveis,
      });
    },
    onSuccess: onCriado,
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  function alternar(recurso: Recurso) {
    setSelecionados((atual) =>
      atual.includes(recurso) ? atual.filter((r) => r !== recurso) : [...atual, recurso],
    );
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Novo perfil de acesso"
      descricao="Permissões em tabela, sem nova publicação do sistema."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao
            disabled={nome.trim().length < 2 || criar.isPending}
            onClick={() => {
              setErro(null);
              criar.mutate();
            }}
          >
            Criar perfil
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        {erro && (
          <div
            className="empty"
            role="alert"
            style={{ borderColor: 'var(--red)', marginBottom: 14 }}
          >
            {erro}
          </div>
        )}
        <Formulario onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Nome do perfil">
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Atendente do bar"
              autoFocus
            />
          </Campo>
          <Campo rotulo="Começar a partir de">
            <select value={origemId} onChange={(e) => setOrigemId(e.target.value)}>
              <option value="">Perfil em branco</option>
              {perfis.map((p) => (
                <option key={p.id} value={p.id}>
                  Copiar de {p.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Permissões iniciais"
            ajuda="o nível de operação — sem custo, sem margem, sem folha"
          >
            <div className="choices">
              {ATALHOS_DE_PERFIL.map(({ recurso }) => (
                <button
                  key={recurso}
                  type="button"
                  className="choice"
                  aria-pressed={selecionados.includes(recurso)}
                  onClick={() => alternar(recurso)}
                >
                  {ROTULO_RECURSO[recurso]}
                </button>
              ))}
            </div>
          </Campo>
        </Formulario>
      </CorpoDeModal>
    </Modal>
  );
}
